import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Stack,
  IconButton,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { buildUndeliveredQuantitySearch } from '../utils/undeliveredQuantityFilters'
import { buildPaymentProcessingSearch } from '../utils/paymentProcessingFilters'
import { useDashboard } from '../hooks/useDashboard'
import DashboardFilterBar from '../components/Dashboard/DashboardFilterBar'
import ExecutiveOverview from '../components/Dashboard/ExecutiveOverview'
import KpiDetailModal from '../components/Dashboard/KpiDetailModal'
import {
  SalesOverviewSection,
  TopEntitiesPanel,
} from '../components/Dashboard/SalesSections'
import {
  CategorySalesSection,
  SalesComparisonSection,
  StockHealthSection,
} from '../components/Dashboard/AnalyticsSections'
import {
  PendingOrdersSection,
  ExpiryRiskSection,
  FastMoversSection,
  PeakHoursHeatmap,
  QuickActionsSection,
} from '../components/Dashboard/OperationalSections'
import { useDashboardFilterOptions } from '../hooks/useDashboardFilterOptions'
import {
  isCustomPeriodPending as isCustomPeriodPendingFn,
  mapGlobalCustomRangeToSectionFilters,
  mapGlobalPeriodToSectionFilters,
} from '../hooks/dashboardPeriod'
import { DASHBOARD_COLORS } from '../components/Dashboard/dashboardFormatters'
import UnreceivedDispatchAlertsSection from '../components/Dashboard/UnreceivedDispatchAlertsSection'

const formatHeaderClock = (date) => {
  const weekday = date.toLocaleDateString('en-GB', { weekday: 'short' })
  const day = date.toLocaleDateString('en-GB', { day: 'numeric' })
  const month = date.toLocaleDateString('en-GB', { month: 'short' })
  const year = date.getFullYear()
  const time = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  return `${weekday}, ${day} ${month} ${year} • ${time}`
}

const DashboardPage = () => {
  const navigate = useNavigate()
  const [dashboardSearchParams] = useSearchParams()
  const {
    data,
    loading,
    sectionLoading,
    error,
    updatedAt,
    filters,
    setFilter,
    refreshAll,
    refreshSection,
  } = useDashboard()

  const sections = data?.sections || {}
  const [modalKey, setModalKey] = useState(null)
  const [modalRows, setModalRows] = useState([])
  const [modalPagination, setModalPagination] = useState({ page: 1, limit: 20, total: 0 })
  const [modalError, setModalError] = useState(null)
  const [modalSearch, setModalSearch] = useState('')
  const [pendingSearch, setPendingSearch] = useState('')
  const [pendingPagination, setPendingPagination] = useState({ page: 1, limit: 5, total: 0 })
  const {
    locations,
    categories,
    partners,
    searchLocations,
    searchCategories,
    searchPartners,
    locationLoading,
    categoryLoading,
    partnerLoading,
  } = useDashboardFilterOptions(
    data?.filterOptions || sections.filterOptions,
    filters,
  )
  const initialLoadCompleteRef = useRef(false)
  const pendingSectionsRef = useRef(new Set())
  const refreshSectionRef = useRef(refreshSection)
  const skipModalSearchEffectRef = useRef(false)
  const skipPendingSearchEffectRef = useRef(true)
  refreshSectionRef.current = refreshSection

  useEffect(() => {
    if (!loading && sections.executiveOverview) {
      initialLoadCompleteRef.current = true
    }
  }, [loading, sections.executiveOverview])

  const refreshSectionIfReady = useCallback(
    (section) => {
      if (!initialLoadCompleteRef.current) return
      if (loading) {
        pendingSectionsRef.current.add(section)
        return
      }
      refreshSectionRef.current(section)
    },
    [loading],
  )

  useEffect(() => {
    if (!loading && initialLoadCompleteRef.current && pendingSectionsRef.current.size > 0) {
      const pending = [...pendingSectionsRef.current]
      pendingSectionsRef.current.clear()
      pending.forEach((section) => refreshSectionRef.current(section))
    }
  }, [loading])
  const isCustomPeriodPending = (period) =>
    isCustomPeriodPendingFn(period, filters.salesStart, filters.salesEnd)

  useEffect(() => {
    if (isCustomPeriodPending(filters.salesRange)) return
    refreshSectionIfReady('salesOverview')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only refetch when sales filters change
  }, [filters.salesRange, filters.salesStart, filters.salesEnd])

  useEffect(() => {
    if (isCustomPeriodPending(filters.outletRange)) return
    refreshSectionIfReady('topOutlets')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.outletRange, filters.salesStart, filters.salesEnd])

  useEffect(() => {
    if (isCustomPeriodPending(filters.partnerRange)) return
    refreshSectionIfReady('topPartnerShops')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.partnerRange, filters.salesStart, filters.salesEnd])

  useEffect(() => {
    if (isCustomPeriodPending(filters.categoryPeriod)) return
    refreshSectionIfReady('categorySales')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.categoryPeriod, filters.categoryOutletId, filters.salesStart, filters.salesEnd])

  useEffect(() => {
    if (isCustomPeriodPending(filters.comparisonPeriod)) return
    refreshSectionIfReady('salesComparison')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.comparisonPeriod, filters.salesStart, filters.salesEnd])

  useEffect(() => {
    refreshSectionIfReady('stockHealth')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.stockView])

  useEffect(() => {
    refreshSectionIfReady('expiryRisk')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.expiryWindow, filters.expiryCategoryId])

  useEffect(() => {
    if (isCustomPeriodPending(filters.fastPeriod)) return
    refreshSectionIfReady('fastMovers')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.fastPeriod, filters.fastCategoryId, filters.salesStart, filters.salesEnd])

  useEffect(() => {
    refreshSectionIfReady('peakHoursHeatmap')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.heatmapOutletId, filters.heatmapWeek])

  useEffect(() => {
    if (sections.pendingOrders?.pagination) {
      setPendingPagination(sections.pendingOrders.pagination)
    }
  }, [sections.pendingOrders])

  const refreshPendingOrders = useCallback(
    async (page = 1, limit = pendingPagination.limit, search = pendingSearch) => {
      const response = await refreshSection('pendingOrders', {
        pendingPage: page,
        pendingLimit: limit,
        ...(search.trim() ? { search: search.trim() } : {}),
      })
      if (response?.sections?.pendingOrders?.pagination) {
        setPendingPagination(response.sections.pendingOrders.pagination)
      }
      return response
    },
    [refreshSection, pendingPagination.limit, pendingSearch],
  )

  const handlePendingPageChange = useCallback(
    async (page) => {
      await refreshPendingOrders(page, pendingPagination.limit, pendingSearch)
    },
    [refreshPendingOrders, pendingPagination.limit, pendingSearch],
  )

  const handlePendingRowsPerPageChange = useCallback(
    async (limit) => {
      await refreshPendingOrders(1, limit, pendingSearch)
    },
    [refreshPendingOrders, pendingSearch],
  )

  useEffect(() => {
    if (skipPendingSearchEffectRef.current) {
      skipPendingSearchEffectRef.current = false
      return undefined
    }
    const timer = setTimeout(() => {
      refreshPendingOrders(1, pendingPagination.limit, pendingSearch)
    }, 350)
    return () => clearTimeout(timer)
    // Page/limit changes are handled by the pagination handlers; only re-query on text.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSearch])

  // Global dashboard reloads omit pending search; re-apply so results stay filtered.
  useEffect(() => {
    if (!pendingSearch.trim()) return undefined
    if (loading) return undefined
    const timer = setTimeout(() => {
      refreshPendingOrders(1, pendingPagination.limit, pendingSearch)
    }, 0)
    return () => clearTimeout(timer)
    // Only re-run when a full dashboard load finishes, not on every pending refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  const fetchModalDetail = useCallback(
    async (key, page = 1, limit = 20, search = modalSearch) => {
      setModalError(null)
      const response = await refreshSection('executiveDetail', {
        kpiDetail: key,
        kpiPage: page,
        kpiLimit: limit,
        ...(search ? { search } : {}),
      })
      if (response?.error) {
        setModalError(response.error)
        setModalRows([])
        return null
      }
      if (!response) return null
      if (response.sectionErrors?.executiveDetail) {
        setModalError(response.sectionErrors.executiveDetail)
        setModalRows([])
        return null
      }
      const detail = response?.sections?.executiveDetail
      if (detail?.rows) setModalRows(detail.rows)
      if (detail?.pagination) setModalPagination(detail.pagination)
      return detail
    },
    [refreshSection, modalSearch],
  )

  const handleKpiClick = useCallback(
    async (key) => {
      if (key === 'awaitingVerificationOrders') {
        const search = buildPaymentProcessingSearch(filters, {
          status: 'awaiting_verification',
        })
        navigate(
          search
            ? `/orders/purchase/payment-processing?${search}`
            : '/orders/purchase/payment-processing?status=awaiting_verification',
          { state: { dashboardSearch: dashboardSearchParams.toString() } },
        )
        return
      }
      if (key === 'rejectedPaymentProofs') {
        const search = buildPaymentProcessingSearch(filters, { status: 'rejected' })
        navigate(
          search
            ? `/orders/purchase/payment-processing?${search}`
            : '/orders/purchase/payment-processing?status=rejected',
          { state: { dashboardSearch: dashboardSearchParams.toString() } },
        )
        return
      }
      if (key === 'undeliveredQuantity') {
        const search = buildUndeliveredQuantitySearch(filters)
        navigate(search ? `/dashboard/undelivered-quantity?${search}` : '/dashboard/undelivered-quantity', {
          state: { dashboardSearch: dashboardSearchParams.toString() },
        })
        return
      }
      skipModalSearchEffectRef.current = true
      setModalKey(key)
      setModalRows([])
      setModalPagination({ page: 1, limit: 20, total: 0 })
      setModalError(null)
      setModalSearch('')
      await fetchModalDetail(key, 1, 20, '')
    },
    [fetchModalDetail, navigate, filters, dashboardSearchParams],
  )

  const handleModalClose = useCallback(() => {
    setModalKey(null)
    setModalError(null)
    setModalSearch('')
  }, [])

  const handleModalRetry = useCallback(() => {
    if (!modalKey) return
    fetchModalDetail(modalKey, modalPagination.page, modalPagination.limit, modalSearch)
  }, [modalKey, modalPagination.page, modalPagination.limit, modalSearch, fetchModalDetail])

  const handleModalPageChange = useCallback(
    (page) => {
      if (!modalKey) return
      fetchModalDetail(modalKey, page, modalPagination.limit, modalSearch)
    },
    [modalKey, modalPagination.limit, modalSearch, fetchModalDetail],
  )

  const handleModalRowsPerPageChange = useCallback(
    (limit) => {
      if (!modalKey) return
      fetchModalDetail(modalKey, 1, limit, modalSearch)
    },
    [modalKey, modalSearch, fetchModalDetail],
  )

  useEffect(() => {
    if (!modalKey) return undefined
    if (skipModalSearchEffectRef.current) {
      skipModalSearchEffectRef.current = false
      return undefined
    }
    const timer = setTimeout(() => {
      fetchModalDetail(modalKey, 1, modalPagination.limit, modalSearch)
    }, 350)
    return () => clearTimeout(timer)
  }, [modalSearch, modalKey, modalPagination.limit, fetchModalDetail])

  const greeting = useMemo(() => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good morning'
    if (hour < 17) return 'Good afternoon'
    return 'Good evening'
  }, [])

  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30000)
    return () => clearInterval(timer)
  }, [])

  const applyGlobalPeriod = useCallback(
    (period) => {
      setFilter(mapGlobalPeriodToSectionFilters(period))
    },
    [setFilter],
  )

  const applyGlobalCustomRange = useCallback(
    ({ start, end }) => {
      setFilter(mapGlobalCustomRangeToSectionFilters({ start, end }))
    },
    [setFilter],
  )

  if (loading && !sections.executiveOverview) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  const updatedLabel = updatedAt
    ? `Live · updated ${Math.max(1, Math.round((Date.now() - updatedAt.getTime()) / 60000))} min ago`
    : 'Loading…'

  return (
    <Container maxWidth={false} sx={{ py: 0, px: 0, maxWidth: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography variant="h4" fontWeight={700}>
            Dashboard
          </Typography>
          <Typography variant="h6" color="text.secondary" fontWeight={400}>
            — {greeting}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              px: 1.75,
              py: 0.85,
              borderRadius: '20px',
              bgcolor: DASHBOARD_COLORS.bg,
              color: '#555550',
              fontSize: '13px',
              fontWeight: 500,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
            }}
          >
            {formatHeaderClock(now)}
          </Box>
          <IconButton
            onClick={refreshAll}
            aria-label="Refresh dashboard"
            sx={{
              width: 40,
              height: 40,
              border: '1px solid',
              borderColor: 'rgba(0,0,0,0.13)',
              borderRadius: '10px',
              bgcolor: '#fff',
              '&:hover': { bgcolor: DASHBOARD_COLORS.bg },
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Typography sx={{ cursor: 'pointer' }} onClick={refreshAll}>Retry</Typography>}>
          {error}
        </Alert>
      )}
      {Object.keys(data?.sectionErrors || {}).length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} action={<Typography sx={{ cursor: 'pointer' }} onClick={refreshAll}>Retry</Typography>}>
          Some dashboard sections could not be loaded
          {Object.keys(data.sectionErrors).length > 0 && (
            <> ({Object.keys(data.sectionErrors).join(', ')})</>
          )}
          . Refresh to retry.
        </Alert>
      )}

      <DashboardFilterBar
        filters={filters}
        setFilter={setFilter}
        locations={locations}
        categories={categories}
        partners={partners}
        onLocationSearch={searchLocations}
        onCategorySearch={searchCategories}
        onPartnerSearch={searchPartners}
        locationLoading={locationLoading}
        categoryLoading={categoryLoading}
        partnerLoading={partnerLoading}
        onGlobalPeriodChange={applyGlobalPeriod}
        onGlobalCustomRangeApply={applyGlobalCustomRange}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={700}>Executive Overview</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px' }}>
              {updatedLabel}
            </Typography>
          </Stack>
          <ExecutiveOverview data={sections.executiveOverview} onKpiClick={handleKpiClick} />
        </Box>

        <UnreceivedDispatchAlertsSection />

        <SalesOverviewSection
          data={sections.salesOverview}
          salesRange={filters.salesRange}
          salesStart={filters.salesStart}
          salesEnd={filters.salesEnd}
          onRangeChange={(salesRange) =>
            setFilter({ salesRange, salesStart: '', salesEnd: '' })
          }
          onCustomRangeChange={(range) => setFilter(range)}
          loading={sectionLoading.salesOverview}
          timezone={data?.timezone}
        />

        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={6}>
            <TopEntitiesPanel
              title="Top Outlets"
              variant="outlet"
              data={sections.topOutlets}
              range={filters.outletRange}
              onRangeChange={(outletRange) => setFilter({ outletRange })}
              loading={sectionLoading.topOutlets}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TopEntitiesPanel
              title="Top Partner Shops"
              variant="partner"
              data={sections.topPartnerShops}
              range={filters.partnerRange}
              onRangeChange={(partnerRange) => setFilter({ partnerRange })}
              loading={sectionLoading.topPartnerShops}
              orderStatus={sections.orderStatus}
              orderStatusLoading={sectionLoading.orderStatus}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
            <CategorySalesSection
              data={sections.categorySales}
              filters={filters}
              locations={locations}
              onLocationSearch={searchLocations}
              locationLoading={locationLoading}
              setFilter={setFilter}
              loading={sectionLoading.categorySales}
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
            <SalesComparisonSection
              data={sections.salesComparison}
              filters={filters}
              setFilter={setFilter}
              loading={sectionLoading.salesComparison}
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
            <StockHealthSection
              data={sections.stockHealth}
              filters={filters}
              setFilter={setFilter}
              loading={sectionLoading.stockHealth}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={4} sx={{ minWidth: 0, display: 'flex' }}>
            <PendingOrdersSection
              data={sections.pendingOrders}
              loading={sectionLoading.pendingOrders}
              pagination={pendingPagination}
              onPageChange={handlePendingPageChange}
              onRowsPerPageChange={handlePendingRowsPerPageChange}
              searchValue={pendingSearch}
              onSearchChange={setPendingSearch}
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ minWidth: 0, display: 'flex' }}>
            <ExpiryRiskSection
              data={sections.expiryRisk}
              filters={filters}
              categories={categories}
              onCategorySearch={searchCategories}
              categoryLoading={categoryLoading}
              setFilter={setFilter}
              loading={sectionLoading.expiryRisk}
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ minWidth: 0, display: 'flex' }}>
            <FastMoversSection
              data={sections.fastMovers}
              filters={filters}
              categories={categories}
              onCategorySearch={searchCategories}
              categoryLoading={categoryLoading}
              setFilter={setFilter}
              loading={sectionLoading.fastMovers}
            />
          </Grid>
        </Grid>

        <PeakHoursHeatmap
          data={sections.peakHoursHeatmap}
          filters={filters}
          locations={locations}
          onLocationSearch={searchLocations}
          locationLoading={locationLoading}
          setFilter={setFilter}
          loading={sectionLoading.peakHoursHeatmap}
        />

        <QuickActionsSection />
      </Box>

      <KpiDetailModal
        open={Boolean(modalKey)}
        onClose={handleModalClose}
        detailKey={modalKey}
        rows={modalRows}
        loading={sectionLoading.executiveDetail}
        error={modalError}
        onRetry={handleModalRetry}
        pagination={modalPagination}
        onPageChange={handleModalPageChange}
        onRowsPerPageChange={handleModalRowsPerPageChange}
        searchValue={modalSearch}
        onSearchChange={setModalSearch}
      />
    </Container>
  )
}

export default DashboardPage
